import { useEffect, useRef, useState } from "react";
import { GROUP_COLOR, STATE_COLOR, nodeActivity } from "@/lib/pipelines";

/* Generic n8n-style canvas renderer for ONE pipeline definition.
 * Grid layout: x = col, y = row (auto-spaced). Pure SVG/CSS, pan + zoom.
 * Lights nodes/edges live from the shared tasks + audit feeds. */

const NW = 156, NH = 80, COL_W = 214, ROW_H = 124, MX = 40, MY = 40;

const nx = (n) => MX + n.col * COL_W;
const ny = (n) => MY + n.row * ROW_H;

const CX = (n) => nx(n) + NW / 2;
const CY = (n) => ny(n) + NH / 2;

// Orthogonal edge routing (n8n-style) — kills diagonal crossings.
// - same column  -> clean vertical curve (top/bottom anchors)
// - forward (L->R) -> right-angle step with rounded corners
// - backward/loop -> routed DOWN through a lane below both nodes
function route(s, t, idx) {
  const dCol = t.col - s.col;
  const r = 9;
  // vertical (same column, different row)
  if (Math.abs(dCol) < 0.6 && Math.abs(t.row - s.row) >= 0.4) {
    const x1 = CX(s), x2 = CX(t);
    if (t.row < s.row) {
      const y1 = ny(s), y2 = ny(t) + NH;
      return { d: `M ${x1},${y1} C ${x1},${y1 - 24} ${x2},${y2 + 24} ${x2},${y2}`, lx: (x1 + x2) / 2, ly: (y1 + y2) / 2 };
    }
    const y1 = ny(s) + NH, y2 = ny(t);
    return { d: `M ${x1},${y1} C ${x1},${y1 + 24} ${x2},${y2 - 24} ${x2},${y2}`, lx: (x1 + x2) / 2, ly: (y1 + y2) / 2 };
  }
  // forward (left -> right)
  if (dCol > 0) {
    const sx = nx(s) + NW, sy = CY(s), tx = nx(t), ty = CY(t);
    if (Math.abs(sy - ty) < 1) return { d: `M ${sx},${sy} L ${tx},${ty}`, lx: (sx + tx) / 2, ly: sy - 5 };
    const mx = sx + Math.max(26, (tx - sx) / 2);
    const dir = ty > sy ? 1 : -1;
    const d = `M ${sx},${sy} L ${mx - r},${sy} Q ${mx},${sy} ${mx},${sy + dir * r} L ${mx},${ty - dir * r} Q ${mx},${ty} ${mx + r},${ty} L ${tx},${ty}`;
    return { d, lx: mx, ly: (sy + ty) / 2 };
  }
  // backward / loop (right -> left): drop to a lane just under the lower node, travel, rise in
  const sx = CX(s), tx = CX(t);
  const laneY = MY + Math.max(s.row, t.row) * ROW_H + NH + 16 + (idx % 5) * 9;
  const sy = ny(s) + NH, ty = ny(t) + NH;
  const d = `M ${sx},${sy} L ${sx},${laneY - r} Q ${sx},${laneY} ${sx - r},${laneY} L ${tx + r},${laneY} Q ${tx},${laneY} ${tx},${laneY - r} L ${tx},${ty}`;
  return { d, lx: (sx + tx) / 2, ly: laneY + 11 };
}

// Accept edges as [from,to,opts] tuples OR {from,to,dashed,label} objects.
function normEdge(e) {
  if (Array.isArray(e)) return { from: e[0], to: e[1], ...(e[2] || {}) };
  return e;
}

// Distinct border style per node kind.
function kindBorder(kind, hot, sc, accent) {
  if (kind === "error") return { radius: 4, border: `1px solid ${hot ? sc : "rgb(196,52,44)"}` };
  if (kind === "decision" || kind === "gate") return { radius: 16, border: `1px dashed ${hot ? sc : "rgb(169,130,28)"}` };
  if (kind === "timer") return { radius: 4, border: `1px dashed ${hot ? sc : "rgb(176,116,18)"}` };
  if (kind === "external") return { radius: 4, border: `1px dashed ${hot ? sc : "rgb(190,176,152)"}` };
  if (kind === "notify") return { radius: 10, border: `1px solid ${hot ? sc : "rgb(36,104,184)"}` };
  return { radius: 4, border: `1px solid ${hot ? sc : "rgb(223,214,198)"}` };
}

export default function PipelineCanvas({ def, tasks, audit, now, onSelect, selectedId }) {
  const [view, setView] = useState({ tx: 0, ty: 0, scale: 0.9 });
  const drag = useRef(null);
  const surfaceRef = useRef(null);
  const byId = Object.fromEntries(def.nodes.map((n) => [n.id, n]));

  // Fit + center the graph in the viewport on load / tab switch.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mc = Math.max(...def.nodes.map((n) => n.col)) + 1;
    const mr = Math.max(...def.nodes.map((n) => n.row)) + 1;
    const gW = MX * 2 + mc * COL_W, gH = MY * 2 + mr * ROW_H;
    const topPad = 168; // leave room for the header + tabs + counters
    const scale = Math.max(0.4, Math.min(1.05, (r.width - 80) / gW, (r.height - topPad - 60) / gH));
    const tx = Math.max(20, (r.width - gW * scale) / 2);
    const ty = topPad + Math.max(0, (r.height - topPad - gH * scale) / 2);
    setView({ tx, ty, scale });
  }, [def.id]);

  const onWheel = (e) => {
    e.preventDefault();
    setView((v) => ({ ...v, scale: Math.min(1.8, Math.max(0.35, v.scale * (e.deltaY < 0 ? 1.1 : 0.9))) }));
  };
  const onDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty }; };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const ntx = d.tx + (e.clientX - d.x), nty = d.ty + (e.clientY - d.y);
    setView((v) => ({ ...v, tx: ntx, ty: nty }));
  };
  const onUp = () => { drag.current = null; };

  const maxCol = Math.max(...def.nodes.map((n) => n.col)) + 1;
  const maxRow = Math.max(...def.nodes.map((n) => n.row)) + 1;
  const W = MX * 2 + maxCol * COL_W, H = MY * 2 + maxRow * ROW_H;

  const act = (n) => nodeActivity(n, tasks, audit, now);

  return (
    <div ref={surfaceRef} className="pj-canvas-grid absolute inset-0" onWheel={onWheel} onMouseDown={onDown}
      onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
      style={{ cursor: drag.current ? "grabbing" : "grab", overflow: "hidden" }}>
      <div style={{ position: "absolute", transformOrigin: "0 0", transform: `translate(${view.tx}px,${view.ty}px) scale(${view.scale})` }}>
        <svg width={W} height={H} style={{ position: "absolute", left: 0, top: 0, overflow: "visible", pointerEvents: "none" }}>
          {def.edges.map((raw, i) => {
            const e = normEdge(raw);
            const s = byId[e.from], t = byId[e.to]; if (!s || !t) return null;
            const hot = act(s).hot || act(t).hot;
            const { d, lx, ly } = route(s, t, i);
            return (
              <g key={i}>
                <path d={d} fill="none"
                  stroke={hot ? "rgb(32,138,78)" : "rgb(190,176,152)"}
                  strokeWidth={hot ? 2 : 1.5}
                  strokeDasharray={e.dashed && !hot ? "3 5" : hot ? "6 6" : "0"}
                  className={hot ? "pj-edge-hot" : ""} opacity={e.dashed && !hot ? 0.5 : 1} />
                {e.label && (
                  <text x={lx} y={ly} textAnchor="middle"
                    style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize: 8.5, fill: hot ? "rgb(32,138,78)" : "rgb(96,82,64)", textTransform: "uppercase", letterSpacing: "0.06em",
                      stroke: "rgb(244,239,230)", strokeWidth: 3, paintOrder: "stroke", strokeLinejoin: "round" }}>
                    {e.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {def.nodes.map((n) => {
          const { state, hot } = act(n);
          const accent = GROUP_COLOR[n.group] || "rgb(140,128,110)";
          const sc = STATE_COLOR[state] || "rgb(140,128,110)";
          const kb = kindBorder(n.kind, hot, sc, accent);
          const idleLabel = { decision: "branch", gate: "gate", timer: "waiting", notify: "ready", error: "guard", external: "external" }[n.kind] || "idle";
          const selected = selectedId === n.id;
          return (
            <div key={n.id} data-testid={`node-${def.id}-${n.id}`} className={hot ? "pj-node-hot" : ""}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onSelect?.(n); }}
              style={{
                position: "absolute", left: nx(n), top: ny(n), width: NW, height: NH, cursor: "pointer",
                background: selected ? "rgb(252,249,243)" : "linear-gradient(180deg, rgb(252,249,243), rgb(244,239,230))", border: kb.border,
                borderLeft: `3px solid ${accent}`, borderRadius: kb.radius,
                outline: selected ? "2px solid rgb(169,130,28)" : "none", outlineOffset: 1,
                padding: "8px 11px", boxSizing: "border-box", transition: "border-color .3s",
                overflow: "hidden", display: "flex", flexDirection: "column",
              }}>
              <div className="flex items-center justify-between">
                <span className="mono text-[12px]" style={{ color: "rgb(38,31,24)", letterSpacing: "0.03em" }}>{n.label}</span>
                <span style={{ width: 7, height: 7, borderRadius: 9, background: state === "idle" ? "rgb(190,176,152)" : sc, boxShadow: hot ? `0 0 8px ${sc}` : "none" }} />
              </div>
              <div className="mono" style={{ fontSize: 9.5, color: "rgb(72,60,46)", marginTop: 3, lineHeight: 1.2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{n.role}</div>
              <div className="mono" style={{ fontSize: 8.5, color: sc, marginTop: "auto", paddingTop: 2, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                {state === "idle" ? idleLabel : state.replace("_", " ")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
